import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChefHat, Clock, Flame, CheckCircle, ArrowLeft, User, Maximize, Minimize } from 'lucide-react';
import { toast } from 'sonner';
import type { OrderWithItems, OrderStatus } from '@/types/order';

const KitchenDisplay = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderWithItems[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/admin/login');
        return;
      }

      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'admin')
        .maybeSingle();

      if (!roles) {
        toast.error('Accesso non autorizzato');
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  const fetchOrders = useCallback(async () => {
    try {
      // Query directly - RLS will handle auth
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          customer_name,
          pickup_time,
          order_note,
          status,
          payment_method,
          is_paid,
          created_at,
          order_items (
            id,
            order_id,
            product_id,
            name_snapshot,
            unit_price_cents,
            quantity,
            gluten_option,
            line_total_cents,
            created_at
          )
        `)
        .in('status', ['da_pagare', 'pagato', 'in_preparazione'])
        .order('pickup_time', { ascending: true, nullsFirst: false });

      if (error) throw error;

      setOrders((data || []) as OrderWithItems[]);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const updateStatus = async (orderId: string, newStatus: OrderStatus) => {
    setUpdating(orderId);
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId);

      if (error) throw error;

      toast.success(newStatus === 'in_preparazione' ? 'In preparazione!' : 'Pronto!');
      fetchOrders();
    } catch (err) {
      console.error('Error updating order:', err);
      toast.error('Errore aggiornamento');
    } finally {
      setUpdating(null);
    }
  };

  const formatPickupTime = (pickup: string | null) => {
    if (!pickup) return '--:--';
    const date = new Date(pickup);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-2xl text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <ChefHat className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Forno</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-lg text-muted-foreground">
            {orders.length} ordini attivi
          </span>
          <Button variant="outline" size="sm" onClick={toggleFullscreen}>
            {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-2xl text-muted-foreground">Nessun ordine attivo</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {orders.map((order) => (
            <Card
              key={order.id}
              className={`p-4 flex flex-col h-full ${
                order.status === 'in_preparazione'
                  ? 'border-orange-500 border-2 bg-orange-500/5'
                  : 'border-border'
              }`}
            >
              {/* Header with pickup time and customer name */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <span className="text-2xl font-bold text-foreground">
                    {formatPickupTime(order.pickup_time)}
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">
                  #{order.id.slice(-4).toUpperCase()}
                </span>
              </div>
              
              {/* Customer name */}
              <div className="flex items-center gap-2 mb-4 text-muted-foreground">
                <User className="h-4 w-4" />
                <span className="font-medium">{order.customer_name}</span>
              </div>

              {/* Pizza list - grows to fill available space */}
              <div className="space-y-2 mb-4 flex-1">
                {order.order_items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between py-1 border-b border-border/50 last:border-0"
                  >
                    <span className="text-lg font-medium text-foreground">
                      {item.quantity}x {item.name_snapshot}
                    </span>
                    {item.gluten_option === 'senza_glutine' && (
                      <span className="text-xs bg-amber-500/20 text-amber-600 px-2 py-0.5 rounded">
                        SG
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Action buttons - always at bottom */}
              <div className="flex gap-2 mt-auto">
                {(order.status === 'da_pagare' || order.status === 'pagato') && (
                  <Button
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={() => updateStatus(order.id, 'in_preparazione')}
                    disabled={updating === order.id}
                  >
                    <Flame className="h-4 w-4 mr-2" />
                    Prepara
                  </Button>
                )}
                {order.status === 'in_preparazione' && (
                  <Button
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                    onClick={() => updateStatus(order.id, 'pronto')}
                    disabled={updating === order.id}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Pronto
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default KitchenDisplay;
